import { t } from 'i18next';
import { each, enforce, skipWhen, test, omitWhen } from 'vest';

import { FormUtils } from '@anyx/shared-utils';


import {
  ButtonTypeAction,
  ButtonTypeBlock,
  ButtonTypeCard,
  PostbackAction,
  UploadedImageFile,
} from '../types';
import { isValidActionURL } from '../utils';

/**
 * Validate image in cards, in Button type blocks
 * */
const cardImageValidationBuilder = (prefix: string) => (card: ButtonTypeCard) => {
  skipWhen(!card.image, () => {
    test(
      `${prefix}.image`,
      FormUtils.requiredField(t('crm.lineMessageForm.fields.image.label', { ns: 'crm' })),
      () => {
        enforce(card.image).condition((image?: UploadedImageFile) => image?.state === 'remote');
      },
      `${prefix}-image`
    );
  });
};

/**
 * Validate label inside actions, in Button type blocks
 * */
const labelValidationBuilder = (prefix: string) => (action: ButtonTypeAction) => {
  test(
    `${prefix}.label`,
    FormUtils.requiredField(t('crm.lineMessageForm.fields.actionLabel.label', { ns: 'crm' })),
    () => {
      enforce(action.label).isNotBlank();
    },
    `${prefix}.label`
  );
};

/*
 * Validate URL inside actions, when the action type is not POSTBACK
 * */
const urlValidationBuilder = (prefix: string) => (action: ButtonTypeAction) => {
  omitWhen(action.postbackAction !== PostbackAction.enum.URL_MANUAL, () => {
    test(
      `${prefix}.url`,
      t('crm.lineMessageForm.validations.invalidUrl', { ns: 'crm' }),
      () => {
        enforce(action.url).condition((url) => isValidActionURL(url));
      },
      `${prefix}.url`
    );
  });
};

const urlVariableValidationBuilder = (prefix: string) => (action: ButtonTypeAction) => {
  omitWhen(action.postbackAction !== PostbackAction.enum.URL_VARIABLE, () => {
    test(
      `${prefix}.urlVariable`,
      FormUtils.requiredField(
        t('crm.lineMessageForm.fields.urlAction.variable.label', {
          ns: 'crm',
        })
      ),
      () => {
        enforce(action.urlVariable).isNotBlank();
      },
      `${prefix}.urlVariable`
    );
  });
};

/*
 * Validate messageId inside action, when the action type is not URL
 * */
const messageIdValidationBuilder = (prefix: string) => (action: ButtonTypeAction) => {
  omitWhen(action.postbackAction !== PostbackAction.enum.POSTBACK, () => {
    test(
      `${prefix}.messageId`,
      t('crm.lineMessagePreview.buttonType.error', { ns: 'crm' }),
      () => {
        enforce(action.messageId).isNotEmpty();
      },
      `${prefix}.messageId`
    );
  });
};

/*
 * Validation provider for the action's properties, in each Button Card
 * */
const actionValidationBuilder = (prefix: string) => {
  const validateLabel = labelValidationBuilder(prefix);
  const validateUrl = urlValidationBuilder(prefix);
  const validateMessageId = messageIdValidationBuilder(prefix);
  const validateUrlVariable = urlVariableValidationBuilder(prefix);

  return {
    validateMessageId,
    validateUrl,
    validateLabel,
    validateUrlVariable,
  };
};

/*
 * Default validation for button's cards in Line Message Dialog
 * */
const cardValidationBuilder = (prefix: string) => (card: ButtonTypeCard) => {
  const validateCardImage = cardImageValidationBuilder(prefix);
  validateCardImage(card);
  each(card.actions, (action, actionIndex) => {
    const actionPrefix = `${prefix}.actions.${actionIndex}`;
    const { validateLabel, validateUrl, validateUrlVariable } =
      actionValidationBuilder(actionPrefix);

    validateLabel(action);
    validateUrl(action);
    validateUrlVariable(action);
  });
};

/*
 * Validate cards in Line Message Preview
 * */
const previewCardValidationBuilder = (prefix: string) => (card: ButtonTypeCard) => {
  each(card.actions, (action, actionIndex) => {
    const actionPrefix = `${prefix}.actions.${actionIndex}`;

    const { validateMessageId } = actionValidationBuilder(actionPrefix);
    validateMessageId(action);
  });
};

/*
 * Provide different validations for the Button Type, depending on each case:
 * Default Form validation
 * Line Message Preview
 * */
export const buttonValidationBuilder = (index: number, prefix?: string) => ({
  validateLineMessageButton: (message: ButtonTypeBlock) => {
    each(message.cards, (card, cardIndex) => {
      const prefix = `messages.${index}.cards.${cardIndex}`;
      const validateCard = cardValidationBuilder(prefix);
      validateCard(card);
    });
  },

  validateLineMessagePreviewButton: (message: ButtonTypeBlock) => {
    each(message.cards, (card, cardIndex) => {
      const cardPrefix = `${prefix}.messages.${index}.cards.${cardIndex}`;
      const validateCard = previewCardValidationBuilder(cardPrefix);
      validateCard(card);
    });
  },
});
